import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { cityService } from "../api/services/cityService";
import {
  CitiesResponse,
  CityFilters,
  UpdateCityPayload,
} from "../schemas/citySchema";

export const useCities = (
  filters: CityFilters = {},
  options?: { enabled?: boolean },
) => {
  const queryClient = useQueryClient();

  const {
    data: cities,
    isLoading: isCitiesLoading,
    refetch,
  } = useQuery<CitiesResponse, Error>({
    queryKey: ["cities", filters],
    queryFn: () => cityService.getAll(filters),
    enabled: options?.enabled !== false,
  });

  const createCityMutation = useMutation({
    mutationFn: cityService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cities"] });
      queryClient.invalidateQueries({ queryKey: ["cityOptions"] });
      toast.success("City created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create City");
    },
  });

  const updateCityMutation = useMutation({
    mutationFn: cityService.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cities"] });
      queryClient.invalidateQueries({ queryKey: ["cityOptions"] });
      toast.success("City updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update City");
    },
  });

  const deleteCityMutation = useMutation({
    mutationFn: cityService.remove,
    onSuccess: (_data, ids) => {
      queryClient.invalidateQueries({ queryKey: ["cities"] });
      queryClient.invalidateQueries({ queryKey: ["cityOptions"] });
      if (Array.isArray(ids) && ids.length > 1) {
        toast.success(`${ids.length} items deleted successfully`);
        return;
      }
      toast.success("City deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete City");
    },
  });

  const deleteCities = (ids: string[]) => {
    deleteCityMutation.mutate(ids);
  };

  return {
    cities,
    refetchCities: refetch,
    createCity: createCityMutation.mutate,
    updateCity: updateCityMutation.mutate,
    deleteCities,
    isLoading:
      isCitiesLoading ||
      createCityMutation.isPending ||
      updateCityMutation.isPending ||
      deleteCityMutation.isPending,
  };
};
